import { useEffect } from 'react'

import { Combobox, Loader } from '@mantine/core'
import { CircleAlert } from 'lucide-react'

import { CarrierOptionCompany } from './carrierOptionDetails'
import CompanyOption from './CompanyOption'

interface CarrierListProps {
  results: CarrierOptionCompany[]
  inputDebounced: string
  searching: boolean
  searchError: Error | null
  minimumCharacters: number
  inViewport: boolean
  searchResults?: {
    searchResults?: {
      pageInfo?: {
        offset: number
        limit: number
      }
      lastPage?: boolean
    }
  }
  onLoadMore?: () => void
  viewportRef?: React.Ref<HTMLDivElement>
}

const CarrierList = ({
  results,
  inputDebounced,
  searching,
  searchError,
  minimumCharacters,
  inViewport,
  searchResults,
  onLoadMore,
  viewportRef,
}: CarrierListProps) => {
  // Trigger infinite scroll when in viewport and conditions are met
  useEffect(() => {
    const { pageInfo, lastPage } = searchResults?.searchResults || {}
    const trimmed = inputDebounced.trim()
    if (
      trimmed.length >= minimumCharacters &&
      results.length &&
      inViewport &&
      !searching &&
      !lastPage &&
      Number.isInteger(pageInfo?.offset) &&
      onLoadMore
    ) {
      onLoadMore()
    }
  }, [
    inViewport,
    inputDebounced,
    results.length,
    searching,
    searchResults?.searchResults,
    minimumCharacters,
    onLoadMore,
  ])

  // Show minimum characters requirement
  if (inputDebounced.trim().length < minimumCharacters) {
    return (
      <Combobox.Empty className="pt-2 text-center text-xs">
        Search DoT company from dropdown
      </Combobox.Empty>
    )
  }

  // Show no results found
  if (results.length === 0 && !searching) {
    if (searchError) {
      return (
        <Combobox.Empty className="flex flex-row items-center justify-center gap-2 pt-2 text-xs text-doubleNickel-warning-500">
          <CircleAlert size={14} className="stroke-doubleNickel-warning-500" />
          Autocomplete is unavailable
        </Combobox.Empty>
      )
    }

    return (
      <Combobox.Empty className="pt-2 text-center text-xs">
        No suggestions found
      </Combobox.Empty>
    )
  }

  // Show carriers and loading state
  return (
    <>
      {results.map((item, index) => (
        <CompanyOption
          key={`${item.dotNumber}-${item.companyName}-${index}`}
          company={item}
          index={index}
        />
      ))}
      {searching && (
        <Combobox.Empty className="pt-2 text-center text-xs">
          <Loader size={18} color="gray" />
        </Combobox.Empty>
      )}
      {/* Viewport detection element for infinite scroll */}
      <Combobox.Empty
        ref={viewportRef}
        className={`flex min-h-1 flex-row items-center ${
          !results.length && !searching ? 'hidden' : ''
        }`}
      />
    </>
  )
}

export default CarrierList
